import { Button } from '@client/components/ui/button';
import { putMultipleFundingSourceForClientByClientIdRequest } from '@client/model/clientModel';
import { setSelectedAccountTypes } from '@client/model/ltcFinancialPlanningModel';
import {
  selectClient,
  selectLtcFinancialPlanningSelectedAccountTypes,
} from '@client/model/selectors';
import { Trash2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useVehiclePartnerActions } from '@client/hooks/use-vehicle-partner-actions';
import { isPartnerSharedRecipient } from '@client/components/funding-sources-coordination/partner-funding-utils';

interface AssetDeleteButtonProps {
  accountType: string;
  fundingSourceId: string | null;
  selfFundingName: string;
  onDeleted?: () => void;
  className?: string;
  variant?: 'icon' | 'button';
}

export function AssetDeleteButton({
  accountType,
  fundingSourceId,
  selfFundingName,
  onDeleted,
  className,
  variant = 'icon',
}: AssetDeleteButtonProps) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const selectedAccountTypes = useSelector(selectLtcFinancialPlanningSelectedAccountTypes);
  const { handleDeleteShared } = useVehiclePartnerActions();

  const source = fundingSourceId ? client.multipleFundingSources.selfFunding[fundingSourceId] : undefined;
  const isShared = source?.partnerAction === 'mirrored' && !!source.fundingSourceGroupId;
  // Only the partner who shared a fund can delete it, and that removes both copies.
  const isReadOnlyShared = isPartnerSharedRecipient(source, client.clientId);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion toggle
    setShowConfirmation(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    setIsDeleting(true);

    try {
      // Step 1: If there's a funding source, delete it from backend
      if (fundingSourceId && isShared) {
        await handleDeleteShared(fundingSourceId);
      } else if (fundingSourceId) {
        dispatch(
          putMultipleFundingSourceForClientByClientIdRequest({
            clientId: client.clientId,
            fundingSourceType: 'selfFunding',
            fundingSourceData: null,
            fundingSourceId,
          }),
        );
      }

      // Step 2: Remove from selectedAccountTypes
      const updatedAccountTypes = selectedAccountTypes.filter(type => type !== accountType);
      dispatch(setSelectedAccountTypes(updatedAccountTypes));

      // Step 3: Call the optional callback
      onDeleted?.();
    } finally {
      setIsDeleting(false);
      setShowConfirmation(false);
    }
  }, [
    fundingSourceId,
    isShared,
    handleDeleteShared,
    accountType,
    selectedAccountTypes,
    dispatch,
    client.clientId,
    onDeleted,
  ]);

  const handleCancelDelete = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setShowConfirmation(false);
  }, []);

  if (isReadOnlyShared) return null;

  return (
    <>
      {/* Delete Button */}
      {variant === 'icon' ? (
        <Button
          size="icon"
          variant="ghost"
          onClick={handleDeleteClick}
          className={`flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600 ${className || ''}`}
          title="Remove this funding source"
          disabled={isDeleting}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      ) : (
        <Button
          type="button"
          onClick={handleDeleteClick}
          variant="destructive"
          className={`rounded-full ${className || ''}`}
          size="sm"
          disabled={isDeleting}
        >
          Delete
        </Button>
      )}

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={handleCancelDelete}
        >
          <div
            className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="mb-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
            </div>

            <h3 className="text-lg font-semibold text-gray-900">Remove Funding Source?</h3>
            <p className="mt-2 text-sm text-gray-700">
              Are you sure you want to remove <span className="font-medium">{selfFundingName}</span>
              ? This action cannot be undone and will delete all associated data.
            </p>

            <div className="mt-6 flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelDelete}
                disabled={isDeleting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                {isDeleting ? 'Removing...' : 'Remove'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
